import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Area } from 'react-easy-crop';
import { falconClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import { extractNumbers } from '@/utils/extract-numbers';

interface Profile {
  id: string;
  name: string;
  first_name: string;
  email: string;
  avatar: string;
  cellphone_number: string;
  birth_date: string;
  personal_email: string;
  company: {
    name: string;
    logo_url: string | null;
  };
}

interface UpdateProfileImageProps {
  cropArea: Area;
  image: string;
}

interface UseProfileUpdateType {
  name: string;
  phone: string;
  birthdate: string;
  personal_email: string;
}

const baseUrl = '/api/account/profile';

export const useProfileShow = () => {
  const response = useQuery<Profile>({
    queryKey: [clientKeys.useProfileShow],
    queryFn: async () => await falconClient.get(baseUrl),
  });

  return {
    ...response,
    profile: response.data,
  };
};

export const useProfileUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      phone,
      birthdate,
      personal_email,
    }: UseProfileUpdateType) => {
      return await falconClient.patch(baseUrl, {
        cellphone_number: extractNumbers(phone),
        birth_date: birthdate,
        personal_email,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useProfileShow],
      });
    },
  });
};

export const useUpdateProfileImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ cropArea, image }: UpdateProfileImageProps) => {
      const res = await falconClient.put(`${baseUrl}/avatar`, {
        ...cropArea,
        image,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useProfileShow],
      });
    },
  });
};

export const useDeleteProfileImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => await falconClient.delete(`${baseUrl}/avatar`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useProfileShow],
      });
    },
  });
};
